import { useEffect, useState } from "react";
import { api, asArray, formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Search, UserPlus, X, Check, Copy, User } from "lucide-react";
import { emailError, phoneError, cleanPhoneInput, collect } from "@/lib/validate";

/**
 * Admin-only: choose which business owner a listing / payment belongs to,
 * or create the owner's account on the spot (a password is generated and shown once).
 * value = { id, name, email, phone } | null
 */
export default function OwnerPicker({ value, onChange, city, testId = "owner-picker", allowNone = true }) {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ name: "", email: "", phone: "" });
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get("/admin/owners", { params: q ? { q } : {} }).then((r) => setList(asArray(r.data).slice(0, 30))).catch(() => setList([]));
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const createOwner = async () => {
    const found = collect({ name: nf.name.trim() ? "" : "Enter a name", email: emailError(nf.email, { required: true }), phone: phoneError(nf.phone) });
    setErrs(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/owners", { ...nf, phone: nf.phone || null, city: city || null });
      onChange({ id: r.data.id, name: r.data.name, email: r.data.email, phone: r.data.phone });
      setCreated(r.data);
      setCreating(false); setNf({ name: "", email: "", phone: "" });
      toast.success("Owner account created");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      {value?.id ? (
        <div className="flex items-center gap-3 rounded-2xl border border-[color:var(--line)] bg-white px-3 py-2.5">
          <span className="w-9 h-9 rounded-full bg-[color:var(--ivory)] text-[color:var(--maroon)] flex items-center justify-center shrink-0"><User className="w-4 h-4"/></span>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[14px] truncate">{value.name || value.email}</div>
            <div className="text-[12px] text-neutral-500 truncate">{[value.email, value.phone].filter(Boolean).join(" · ")}</div>
          </div>
          <button type="button" onClick={() => { onChange(null); setCreated(null); setOpen(true); }} className="text-[13px] font-semibold text-[color:var(--maroon)] underline underline-offset-4" data-testid={`${testId}-change`}>Change</button>
        </div>
      ) : creating ? (
        <div className="rounded-2xl border border-[color:var(--gold)]/60 bg-white p-3 space-y-2">
          <div className="flex items-center justify-between"><div className="text-[13px] font-semibold text-[color:var(--maroon)]">New business owner</div><button type="button" onClick={() => setCreating(false)} className="p-1 text-neutral-500" aria-label="Cancel"><X className="w-4 h-4"/></button></div>
          <div className="grid sm:grid-cols-3 gap-2">
            <div><input value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Owner name" className={`input !py-2 ${errs.name ? "invalid" : ""}`} data-testid={`${testId}-new-name`}/>{errs.name && <span className="field-error">{errs.name}</span>}</div>
            <div><input type="email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder="Email (login)" className={`input !py-2 ${errs.email ? "invalid" : ""}`} data-testid={`${testId}-new-email`}/>{errs.email && <span className="field-error">{errs.email}</span>}</div>
            <div><input type="tel" value={nf.phone} onChange={(e) => setNf({ ...nf, phone: cleanPhoneInput(e.target.value) })} placeholder="Mobile" className={`input !py-2 ${errs.phone ? "invalid" : ""}`} data-testid={`${testId}-new-phone`}/>{errs.phone && <span className="field-error">{errs.phone}</span>}</div>
          </div>
          <button type="button" disabled={busy} onClick={createOwner} className="btn-wine !py-2 !px-4 text-sm" data-testid={`${testId}-create`}><Check className="w-4 h-4"/> {busy ? "Creating…" : "Create owner"}</button>
        </div>
      ) : (
        <div className="rounded-2xl border border-[color:var(--line)] bg-white">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[color:var(--gold)]"/>
            <input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Search owners by name, email or phone" className="w-full bg-transparent pl-10 pr-3 py-2.5 text-[14px] outline-none rounded-2xl" data-testid={`${testId}-search`}/>
          </div>
          {open && (
            <div className="border-t border-[color:var(--line)] max-h-56 overflow-y-auto">
              {allowNone && <button type="button" onClick={() => { onChange(null); setOpen(false); }} className="w-full text-left px-3.5 py-2 text-[13px] text-neutral-500 hover:bg-[color:var(--ivory)]">No owner (managed by DartMyVenue)</button>}
              {list.map((o) => (
                <button type="button" key={o.id} onClick={() => { onChange({ id: o.id, name: o.name, email: o.email, phone: o.phone }); setOpen(false); }} className="w-full text-left px-3.5 py-2 hover:bg-[color:var(--ivory)]" data-testid={`${testId}-opt-${o.id}`}>
                  <div className="text-[14px] font-medium truncate">{o.name || o.email}</div>
                  <div className="text-[12px] text-neutral-500 truncate">{[o.email, o.phone, o.city].filter(Boolean).join(" · ")}</div>
                </button>
              ))}
              {!list.length && <div className="px-3.5 py-2 text-[13px] text-neutral-500">No owners found.</div>}
            </div>
          )}
          <button type="button" onClick={() => { setCreating(true); setOpen(false); setNf((x) => ({ ...x, name: q && !q.includes("@") ? q : x.name, email: q.includes("@") ? q : x.email })); }} className="w-full border-t border-[color:var(--line)] px-3.5 py-2.5 text-left text-[13px] font-semibold text-[color:var(--maroon)] inline-flex items-center gap-2 hover:bg-[color:var(--ivory)] rounded-b-2xl" data-testid={`${testId}-new`}>
            <UserPlus className="w-4 h-4"/> Create a new owner account
          </button>
        </div>
      )}
      {created && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-[13px] text-emerald-900 flex items-center gap-2 flex-wrap" data-testid={`${testId}-credentials`}>
          Login for {created.email}: password <b className="font-mono">{created.password}</b>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(`Email: ${created.email}\nPassword: ${created.password}\nSign in: ${window.location.origin}/login`); toast.success("Copied"); }} className="inline-flex items-center gap-1 underline"><Copy className="w-3.5 h-3.5"/> Copy</button>
          <span className="text-emerald-700/80">Share it with the owner; they can change it later.</span>
        </div>
      )}
    </div>
  );
}
